import { BackOfficeCartridgeStatus } from '@markant-platform/shared/cartridge/model';
import {
  BulkPrice,
  LegalNoticeLinkType,
  Price,
  PriceRequestType,
  Questionnaire,
} from '@markant-platform/cartridge/model';
import { OrganizationStatus, OrganizationType, QualificationLevel } from 'data-model';

export type BackofficeCartridge = {
  id: string;
  contentId?: string;
  key?: string;
  name: string;
  description: string;
  cartridgeServicePackages?: BackofficeCartridgeServicePackage[];
  variants: BackofficeCartridgeVariant[];
  status: BackOfficeCartridgeStatus;
  cartridgeGroupKey: string;
  cartridgeGroupName: string;
  cartridgeGroupCmsId: string;
  legalNoticeLinks: LegalNoticeLinkType[];
  version: number;
};

export type BackofficeCartridgeGroups = {
  cartridgeGroup: {
    id: string;
    key: string;
    name: string;
  };
  cartridges: BackofficeGroupServices[];
};

export type BackofficeGroupServices = {
  id: string;
  name: string;
  status: BackOfficeCartridgeStatus;
};

export type BackofficeCartridgeVariant = {
  variantId: string;
  contentId?: string;
  name: string;
  demo?: boolean;
  description: string;
  longDescription?: string;
  calculationParameters?: string[];
  prices?: BackofficePrice[];
  widgets?: BackofficeWidget[];
  variantStatus: BackOfficeCartridgeStatus;
  allowUiBooking: boolean;
  setUpCost?: BackofficePrice;
  marketingTargetAudience?: TargetAudience[];
  contractPeriod?: number;
  conditionType?: string;
  costUnit?: string;
  costCenter?: string;
  createdAt: string;
  requireIntegrationProject: boolean;
  type: BackofficeVariantType;
  usagePeriodInDays?: boolean;
};

export enum BackofficeVariantType {
  DEMO = 'DEMO',
  E_SERVICE = 'E_SERVICE',
  STANDARD = 'STANDARD',
}

export type BackofficeCartridgeUpdateVariant = Partial<BackofficeCartridgeVariant> & {
  variantId: string;
  requireIntegrationProject: boolean;
};

export type BackofficeWidget = {
  id: string;
  displayName: string;
  type: BackofficeWidgetType;
};

export type BackofficeWidgetType = 'FOCUS_VIEW' | 'EXTERNAL_LINK';

export type BackofficeCartridgeServicePackage = {
  id: string;
  name: string;
  cartridgeServices: BackofficeCartridgeService[];
};

export type BackofficeCartridgeService = {
  id: string;
  name: string;
};

export type BookingCartridgeVariantData = {
  cartridgeVariantId: string;
  cartridgeVariantName: string;
  isPlatformBillingActive?: boolean;
  price?: Price;
};

export type BookingCartridgeData = {
  cartridgeId: string;
  cartridgeName: string;
  bookingCartridgeVariants: BookingCartridgeVariantData[];
};

export type BookingEzrDraft = {
  organizationId: string;
  cartridgeBookingRequests: {
    cartridgeId: string;
    cartridgeVariantId: string;
    platformBilling: boolean;
    overwriteCentAmount: number | null;
    bookingReference: string;
    userId: string;
    userDetails: BookingUserDetails;
  }[];
};

export type BookingDemoDraft = {
  cartridgeVariantId: string;
  user: BookingUserDetails;
};

export type BookingUserDetails = {
  salutation?: string;
  email: string;
  firstname: string;
  lastname: string;
  language?: string;
  position?: string;
  phone?: string;
  organization: BookingOrganization;
};

export type BookingOrganization = {
  organizationId: string;
  name: string;
  gln: string;
  address: BookingOrganizationAddress | null;
  organizationType?: string;
  qualificationLevel?: QualificationLevel | null;
  ezrCustomer: boolean;
  billingEmail?: string;
  status?: OrganizationStatus;
  masterKin?: string;
  furtherKins?: string[];
  crmId?: string;
  customerNumber?: string;
  customerNumberSearch?: string;
  billingLanguageIsoCode?: string;
  version?: number;
};

export type BookingOrganizationAddress = {
  street: string;
  streetNumber: string;
  zipCode: string;
  city: string;
  countryCode: string;
};

export type BookingData = {
  id: string;
  cartridgeId: string;
  cartridgeVariantId: string;
  cartridgeVariantName: string;
  cartridgeVariantDescription?: string;
  price: BackofficePrice;
  bookingDate: string;
  lastModifiedAt: string;
  user: string;
  organizationId: string;
  organizationName: string;
  bookingState: BookingState;
  version: number;
  cancellationDate?: string;
  workflowFinishedAt?: string;
  startedBookingByUserName?: string;
  bookingStartedAt?: string;
};

export type BackofficePrice = {
  currency: string; // ISO 3-Letter Currency Code
  centAmount: number;
  priceModel: PriceModel;
  hasToBeRequested: boolean;
  billingPeriod: BillingPeriod;
  targetAudience: TargetAudience;
  questionnaire?: Questionnaire;
  bulkPrices?: BulkPrice[];
};

export type TargetAudience = {
  organizationType: OrganizationType | null;
  qualificationLevel: string;
  ezr: boolean | null;
};

export type BulkPriceModel = 'PRICE_SCALE_PER_UNIT' | 'STAGGERED_FIXED_PRICE' | 'PRICE_LEVEL_PER_UNIT';
export type PriceModel = 'SUBSCRIPTION' | 'PAY_PER_USE' | 'FIXED_PRICE' | BulkPriceModel;

export type BillingPeriod = 'NONE' | 'QUARTERLY' | 'MONTHLY' | 'YEARLY' | 'HALF_YEARLY';

export enum BookingState {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  BOOKED = 'BOOKED',
  TO_BE_CANCELLED = 'TO_BE_CANCELLED',
  CANCELLED = 'CANCELLED',
  APPROVED = 'APPROVED',
  AUTOMATICALLY_APPROVED = 'AUTOMATICALLY_APPROVED',
  UPGRADED = 'UPGRADED',
  ENDED = 'ENDED',
}

export type BackofficePriceRequestState =
  // States visible to organization admin only:
  | 'REQUESTED' // Organization admin did a price request
  | 'PRICE_OFFER' // Service admin sent a price offer
  | 'ACCEPTED' // Organization admin accepted the offer
  | 'REJECTED' // Organization admin rejected the offer
  | 'OFFER_EXPIRED' // Offer expired
  | 'RETRACTED' // Organization admin retracted the price request
  | 'PRICE_REQUEST_REJECTED_BY_LA' // Service admin rejected the price request
  // States visible to service admin only:
  | 'OPEN' // Service admin has opened a price request and then set the status to "in progress"
  | 'IN_PROGRESS' // Service admin is working on a price request
  | 'OFFER_SENT' // Service Admin sent the price offer
  | 'OFFER_ACCEPTED' // Organization admin accepted the offer
  | 'OFFER_REJECTED' // Organization admin rejected the offer
  | 'PRICE_REQUEST_REJECTED' // Service admin rejected the price request
  // States invisible for both admins but needed for backend-requests:
  | 'OFFER_EXPIRES_SOON' // Offer expires soon
  | 'OFFER_EXPIRES_SOON_2'; // Offer expires soon next step

export const PriceRequestStates: BackofficePriceRequestState[] = [
  'OPEN',
  'RETRACTED',
  'IN_PROGRESS',
  'OFFER_SENT',
  'OFFER_EXPIRES_SOON',
  'OFFER_EXPIRES_SOON_2',
  'OFFER_EXPIRED',
  'OFFER_REJECTED',
  'OFFER_ACCEPTED',
  'PRICE_REQUEST_REJECTED',
];

export enum PriceRequestTransaction {
  CreateUpdateOfferDraft = 'CreateUpdateOfferDraft',
  SendOffer = 'SendOffer',
  RejectOffer = 'RejectOffer',
  RejectPriceRequest = 'RejectPriceRequest',
  RetractPriceRequest = 'RetractPriceRequest',
  AddComment = 'AddComment',
}

export type ContactPerson = {
  salutation: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
};

export type PriceRequestBookingData = {
  cartridgeId: string;
  cartridgeVariantId: string;
  cartridgeName: string;
  cartridgeVariantName: string;
};

export type PriceRequestOrganizationAdminData = {
  id: string;
  cartridge: PriceRequestBookingData;
  requestDate: string;
  state: BackofficePriceRequestState;
  requesterId: string;
  requesterName: string;
  offerParameter?: string;
  offerPrice?: BackofficePrice;
  questionnaire?: AnsweredQuestion[];
  furtherContactPerson?: ContactPerson;
  offerCreatedAt: string;
  offerValidUntil: string;
  offerNumber?: string;
  offerRejection?: BackofficeRejection;
  priceRequestRejection?: BackofficeRejection;
  priceRequestRetraction?: BackofficeRejection;
  priceRequestAcceptance?: BackofficeAccept;
  type?: PriceRequestType;
  version: number;
};

export type BackofficeRejection = {
  rejectedBy: string;
  rejectedAt: string;
  reason: string;
};

export type BackofficeAccept = {
  acceptedBy: string;
  acceptedAt: string;
  reason: string;
};

export type PriceRequestServiceAdminData = {
  id: string;
  cartridge: PriceRequestBookingData;
  requestDate: string;
  state: BackofficePriceRequestState;
  user: BookingUserDetails;
  organization: BookingOrganization;
  requesterName: string;
  offerCreatedAt?: string;
  offerValidUntil?: string;
  offerNumber?: string;
  offerParameter?: string;
  offerPrice: {
    centAmount?: number;
    currency: string;
    billingPeriod: BillingPeriod;
    priceModel: PriceModel;
  };
  questionnaire?: AnsweredQuestion[];
  furtherContactPerson?: ContactPerson;
  offerRejection?: BackofficeRejection;
  priceRequestRejection?: BackofficeRejection;
  priceRequestRetraction?: BackofficeRejection;
  comments: Comment[];
  offerCreatedBy?: string;
  lastModifiedAt: string;
  type?: PriceRequestType;
  version: number;
};

export enum PriceRequestSortField {
  'cartridgeName' = 'CARTRIDGE_NAME',
  'requestDate' = 'REQUEST_DATE',
  'lastModifiedAt' = 'LAST_MODIFIED_AT',
}

export enum PriceRequestSortOrder {
  'asc' = 'asc',
  'desc' = 'desc',
}

export type AnsweredQuestion = {
  question: string;
  answer: string;
  infoText: string;
  group: BackofficeQuestionGroup;
};

export type BackofficeQuestionGroup = 'COMMON' | 'PRICE_PARAMETER';

export type Comment = { createdBy: string; createdAt: string; comment: string };
